import { css } from '@emotion/react';
import { CoversheetTemplate } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/messages.pb';
import { motion } from 'motion/react';
import { VirtuosoGrid } from 'react-virtuoso';
import { EmptyStates } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { SpinningLoader, Text } from '@frontend/design-system';
import { EmptyStateGraphic } from '../empty-state';
import { TemplateCard } from './template-card';

type TemplateListProps = {
  locationId: string;
  templates?: CoversheetTemplate[];
  isLoading: boolean;
  isFetching?: boolean;
  disableTemplates?: boolean;
  isError: boolean;
  contactId?: string;
  draftId?: string;
  trackingId?: string;
  getNextPage?: () => void;
};

export const TemplateList = ({
  locationId,
  templates,
  isLoading,
  isFetching,
  disableTemplates,
  isError,
  contactId,
  draftId,
  trackingId,
  getNextPage,
}: TemplateListProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('fax');

  return (
    <div
      css={{
        marginBottom: theme.spacing(0.5),
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        flexGrow: 1,
        alignItems: 'center',
      }}
    >
      {isLoading ? (
        <SpinningLoader />
      ) : isError ? (
        <EmptyStates
          emptyStateConfig={{
            type: 'fax',
            header: t('Something went wrong. Please try again.'),
          }}
        />
      ) : !templates?.length ? (
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <EmptyStateGraphic />
          <motion.div
            css={{
              marginTop: theme.spacing(2.5),
            }}
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Text color='subdued' textAlign='center' css={{ lineHeight: theme.spacing(3) }}>
              {t('Nothing to see here...yet.')}
            </Text>
            <Text
              color='primary'
              weight='bold'
              textAlign='center'
              css={{ cursor: 'pointer' }}
              onClick={() => {
                navigate({
                  to: '/fax/drafts/template-editor',
                  search: {
                    selectedLocationId: locationId,
                  },
                });
              }}
            >
              {t('Create Fax template')}
            </Text>
          </motion.div>
        </div>
      ) : templates?.length ? (
        <VirtuosoGrid
          css={css`
            width: 100%;
            .virtuoso-grid-list {
              display: flex;
              flex-wrap: wrap;
              gap: ${theme.spacing(2)};
              .virtuoso-grid-item {
                width: calc(33.33% - ${theme.spacing(1.35)});
                @media (max-width: ${breakpoints.medium.max}px) {
                  width: calc(50% - ${theme.spacing(1.35)});
                }
                @media (max-width: ${breakpoints.small.min}px) {
                  width: 100%;
                }
              }
            }
          `}
          data={templates}
          totalCount={templates.length}
          itemContent={(_index, template) => (
            <TemplateCard
              key={template.id}
              isTemplateDataLoading={isLoading || isFetching}
              disableTemplates={disableTemplates}
              template={template}
              locationId={locationId}
              contactId={contactId}
              previousDraftId={draftId}
              trackingId={trackingId}
            />
          )}
          endReached={getNextPage}
        />
      ) : null}
    </div>
  );
};
